import { useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { currentPostAtom } from "../../atom";

import styled from "styled-components";
import { Button, Divider, Modal, notification, Spin, Tabs } from "antd";

import { BarChart, LineChart, PieChart } from "../dashboard";
import { BarChartProps } from "../dashboard/BarChart";
import { PieChartProps } from "../dashboard/PieChart";
import {
  getStatistic,
  getFileList,
  postStatisticExcel,
  deleteStatisticFile,
  downloadStatisticFile,
  getOncoplot,
  postOncoplotExcel,
  postOncoplotGraph,
  getOncoplotImg,
} from "../../api/statisticAPI";
import { useParams } from "react-router-dom";
import { getPostData } from "../../api/postAPI";

const Dashboard = () => {
  const [file, setFile] = useState<any>();
  const [genderData, setGenderData] = useState<PieChartProps>({
    title: "Sex",
    data: [],
  });
  const [ageData, setAgeData] = useState<BarChartProps>({
    title: "Age",
    keys: ["count"],
    index: "age",
    data: [],
  });
  const [siteData, setSiteData] = useState<PieChartProps>({
    title: "Site",
    data: [],
  });
  const [subtypeData, setSubtypeData] = useState<PieChartProps>({
    title: "Subtype",
    data: [],
  });
  const [OPdayData, setOPdayData] = useState<BarChartProps>({
    title: "First OP year",
    keys: ["count"],
    index: "year",
    data: [],
  });
  const [recurdayData, setRecurdayData] = useState<BarChartProps>({
    title: "First Recur date",
    keys: ["count"],
    index: "year",
    data: [],
  });
  const [deathdayData, setDeathdayData] = useState<BarChartProps>({
    title: "Overall survival",
    keys: ["count"],
    index: "year",
    data: [],
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [oncoplotFile, setOncoplotFile] = useState<any>({});
  const [oncoplotImg, setOncoplotImg] = useState<any>("");
  const [isOncoplot, setIsOncoplot] = useState<boolean | null>(null);
  const [isGraphLoading, setIsGraphLoading] = useState<boolean>(false);
  const [isReloadGraphLoading, setIsReloadGraphLoading] = useState<boolean>(false);
  const [visibleOncBox, setVisibleOncBox] = useState<boolean>(false);
  const param = useParams();
  const [api, contextHolder] = notification.useNotification();

  const currentPost = useRecoilValue(currentPostAtom);
  const setCurrentPost = useSetRecoilState(currentPostAtom);

  const getData = () => {
    getStatistic(currentPost.id).then((data) => {
      setGenderData({
        ...genderData,
        data:
          (data && data.genderData?.filter((prev: any) => prev.id !== null)) ||
          [],
      });
      setAgeData({
        ...ageData,
        data:
          (data && data.ageData?.filter((prev: any) => prev.age !== null)) ||
          [],
      });
      setSiteData({
        ...siteData,
        data:
          (data && data.siteData?.filter((prev: any) => prev.id !== null)) ||
          [],
      });
      setSubtypeData({
        ...subtypeData,
        data:
          (data && data.subTypeData?.filter((prev: any) => prev.id !== null)) ||
          [],
      });
      setOPdayData({
        ...OPdayData,
        data:
          (data && data.opDay?.filter((prev: any) => prev.year !== null)) || [],
      });
      setRecurdayData({
        ...recurdayData,
        data:
          (data &&
            data.firstRecurDate?.filter((prev: any) => prev.year !== null)) ||
          [],
      });
      setDeathdayData({
        ...deathdayData,
        data:
          (data && data.deathDate?.filter((prev: any) => prev.year !== null)) ||
          [],
      });
    });

    getOncoplot(currentPost.id).then((data) => {
      setOncoplotFile(data);
    });

    getFileList(currentPost.id).then((data) => {
      setFileList(data);
    });
  };

  const deleteData = async (fileId: number) => {
    if (window.confirm("해당 데이터 파일을 삭제하시겠습니까?")) {
      const res = await deleteStatisticFile(fileId);

      if (res === false) {
        window.alert("권한이 없습니다");
      }

      getData();
    }
  };

  const downloadData = async (fileId: number, originalFilename: string) => {
    if (window.confirm("해당 데이터 파일을 다운로드 하시겠습니까?")) {
      const data = await downloadStatisticFile(fileId);

      if (data !== false && typeof data === "object") {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", originalFilename);
        document.body.appendChild(link);
        link.click();
      } else {
        window.alert("권한이 없습니다");
      }
    }
  };

  const uploadData = async () => {
    const res = await postStatisticExcel(currentPost.id, file[0]);

    if (res && !(res.constructor === Object && Object.keys(res).length === 0)) {
      window.alert("파일이 업로드 되었습니다.");
    }

    getData();
  };

  const reloadOncoplotData = async (oncoplotFileId: number) => {
    if (!window.confirm("Oncoplot graph를 새로고침 하시겠습니까?")) return;
    setIsReloadGraphLoading(true);
    if (oncoplotFileId) {
      const result = await postOncoplotGraph(oncoplotFileId);

      if (result && result.resultCode === "201") {
        api.open({
          type: "success",
          message: "Oncoplot 이미지가 새로고침 되었습니다.",
        });
        getData();
      } else {
        api.open({
          type: "error",
          message: (
            <>
              Oncoplot 이미지 생성 중 오류가 발생했습니다.
              <br />
              관리자에게 문의해 주세요.
            </>
          ),
        });
      }
      setIsReloadGraphLoading(false);
    }
  }

  const uploadOncoplotData = async () => {
    const res = await postOncoplotExcel(currentPost.id, file[0]);

    if (res && !(res.constructor === Object && Object.keys(res).length === 0)) {
      setIsGraphLoading(true);

      const result = await postOncoplotGraph(res);

      if (result && result.resultCode === "201") {
        api.open({
          type: "success",
          message: "파일이 업로드 되었습니다.",
        });
      }else{
        api.open({
          type: "error",
          message: (
            <>
              Oncoplot 이미지 생성 중 오류가 발생했습니다.
              <br />
              관리자에게 문의해 주세요.
            </>
          ),
        });
        
      }
      setIsGraphLoading(false);

    }

    getData();
  };

  // 최초 렌더링 포스트 조회
  useEffect(() => {
    getData();
  }, []);

  // 실시간 반영 - refetch
  useEffect(() => {
    getData();
  }, [currentPost]);

  useEffect(() => {
    if (oncoplotFile) {
      let oncURL: any;
      getOncoplotImg(oncoplotFile?.id).then((data) => {
        if (data) {
          const blob =
            data instanceof Blob
              ? data
              : new Blob([data], { type: "image/jpeg" });

          oncURL = window.URL.createObjectURL(blob);
          oncURL && setIsOncoplot(true);

          setOncoplotImg(oncURL);
        }
      });

      return () => {
        URL.revokeObjectURL(oncURL);
      };
    }
  }, [oncoplotFile]);

  // param id가 변할 경우 param id로 새로운 포스트 데이터 조회 후 current 값 업데이트
  useEffect(() => {
    const settingPost = async () => {
      if (param) {
        let cid = param.id;
        if (cid !== undefined) {
          const id = +cid;
          setCurrentPost(await getPostData(id));
        }
      }
    };

    settingPost();
  }, [param]);

  return (
    <>
      {contextHolder}
      <div>
        <Divider style={{ fontSize: "1.4rem" }}>Dashboard</Divider>
        <DashboardBox>
          <EditButtonWrapper>
            <Button onClick={() => setShowModal(true)}>Edit Data</Button>
            {/* TODO: 그래프 리로드 로딩 */}
            <Button
              // onClick={async () => await postOncoplotGraph(oncoplotFile?.id)}
              disabled={isReloadGraphLoading}
              onClick={() => reloadOncoplotData(oncoplotFile?.id)}
            >
              Reload Graph
            </Button>
            {isReloadGraphLoading && (
              <div>
              파일을 처리중입니다. 잠시만 기다려주세요.
              <Spin style={{ marginLeft: "1rem" }} />
            </div>)}
            
          </EditButtonWrapper>
          {/* Sex */}
          <GenderChartBox>
            <PieChart props={genderData} />
          </GenderChartBox>

          {/* Age */}
          <AgeChartBox>
            <BarChart props={ageData} />
          </AgeChartBox>

          {/* Site */}
          <FullWidthChartBox>
            <PieChart props={siteData} />
          </FullWidthChartBox>

          {/* Sub-type */}
          <FullWidthChartBox>
            <PieChart props={subtypeData} />
          </FullWidthChartBox>

          {/* Op-date */}
          <FullWidthChartBox>
            <BarChart props={OPdayData} />
          </FullWidthChartBox>

          {/* Recur-date */}
          <FullWidthChartBox>
            <LineChart props={recurdayData} />
          </FullWidthChartBox>

          {/* death-date */}
          <FullWidthChartBox>
            <BarChart props={deathdayData} />
          </FullWidthChartBox>

          {/* oncomplot */}
          {isOncoplot && oncoplotImg && (
            <FullWidthOncBox
              style={{ display: visibleOncBox ? "block" : "none" }}
            >
              <img
                className="onc_img"
                src={oncoplotImg}
                alt="onc_img"
                onLoad={() => setVisibleOncBox(true)}
                onError={() => setVisibleOncBox(false)}
              />
            </FullWidthOncBox>
          )}
        </DashboardBox>
        <Modal
          centered
          title="Dashboard Data"
          open={showModal}
          footer={null}
          onCancel={() => setShowModal(false)}
        >
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                label: "Basic",
                key: "1",
                children: (
                  <>
                    <div
                      style={{
                        border: "1px solid black",
                        borderRadius: "5px",
                        padding: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          marginBottom: "0.4rem",
                        }}
                      >
                        Data File List
                      </div>
                      {fileList?.map((file, idx) => (
                        <div
                          key={`${file?.statisticFileId}_${idx}`}
                          style={{
                            borderTop: "1px dotted gray",
                            display: "grid",
                            gridTemplateColumns: "auto 10.5rem",
                            padding: "0.2rem",
                          }}
                        >
                          <div>{file.originalFileName}</div>
                          <div style={{ display: "flex", gap: "5px" }}>
                            <Button
                              onClick={() =>
                                downloadData(
                                  file.statisticFileId,
                                  file.originalFileName
                                )
                              }
                            >
                              Download
                            </Button>
                            <Button
                              onClick={() => deleteData(file.statisticFileId)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: "1rem", marginBottom: "0.4rem" }}>
                      Upload Data
                    </div>
                    <input
                      type="file"
                      name="file"
                      onChange={(event) => setFile(event.target.files)}
                    />
                    <Button onClick={uploadData}>Submit</Button>
                  </>
                ),
              },
              {
                label: "Oncoplot (beta)",
                key: "2",
                children: (
                  <>
                    <div
                      style={{
                        border: "1px solid black",
                        borderRadius: "5px",
                        padding: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          marginBottom: "0.4rem",
                        }}
                      >
                        Data File
                      </div>
                      <div
                        style={{
                          borderTop: "1px dotted gray",
                          display: "grid",
                          gridTemplateColumns: "auto 10.5rem",
                          padding: "0.2rem",
                        }}
                      >
                        <div>{oncoplotFile.originalFileName}</div>
                        {/* <div style={{ display: "flex", gap: "5px" }}>
                          <Button
                            onClick={() =>
                              downloadData(
                                file.statisticFileId,
                                file.originalFileName
                              )
                            }
                          >
                            Download
                          </Button>
                          <Button
                            onClick={() => deleteData(file.statisticFileId)}
                          >
                            Delete
                          </Button>
                        </div> */}
                      </div>
                    </div>
                    <div style={{ fontSize: "1rem", marginBottom: "0.4rem" }}>
                      Upload Data
                    </div>
                    {isGraphLoading ? (
                      <div>
                        파일을 처리중입니다. 잠시만 기다려주세요.
                        <Spin style={{ marginLeft: "1rem" }} />
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          name="file"
                          onChange={(event) => setFile(event.target.files)}
                        />
                        <Button onClick={uploadOncoplotData}>Submit</Button>
                      </>
                    )}
                  </>
                ),
              },
            ]}
          />
        </Modal>
      </div>
    </>
  );
};

const StChartBox = styled("div")`
  display: inline-block;
  margin: 0.2rem;
  border: 1px solid gray;
  background-color: white;
`;

const DashboardBox = styled.div`
  width: 100%;
  max-width: 84rem;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const EditButtonWrapper = styled.div`
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  gap: 0.8rem;
`;

const GenderChartBox = styled(StChartBox)`
  width: calc(25% - 0.5rem);
  height: 20rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AgeChartBox = styled(StChartBox)`
  width: calc(75% - 0.5rem);
  height: 20rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FullWidthChartBox = styled(StChartBox)`
  width: 100%;
  height: 20rem;
`;

const FullWidthOncBox = styled.div`
  margin: 0.2rem;
  border: 1px solid gray;
  background-color: white;
  width: 100%;

  .onc_img {
    width: 100%;
    height: transparent;
  }
`;

export default Dashboard;
